import React, { useState, useCallback, useRef, useEffect, useImperativeHandle } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  useTheme,
  FormControlLabel,
  Switch,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info,
  Delete,
  Visibility
} from '@mui/icons-material';
import { useImportExport } from '../context';
import { ImportSectionProps } from '../types/ComponentTypes';
import { ValidationError } from '../types/ImportExportTypes';
import FileUploadMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/FileUploadMAIQ';
import ComparePreview, { ComparePreviewRef } from './ComparePreview';
import ImportStepIndicator from './ImportStepIndicator';
import JsonDiffViewer from './JsonDiffViewer';
import ImportExportApiService from '../apiService/importExportApiService';
import { validateFileSizeWithUncompressedEstimate } from '../utils/validationUtils';
import { createFileSizeError, formatErrorForUI, logFileSizeError } from '../utils/errorUtils';
import { removeEmptyStringFields, parseJsonStringsInData } from '../utils/dataUtils';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { selectDealerSelected as selectDealerSelectedAIAvatar } from 'app/NewAIAvatarLayout/redux/headerSlice/selectors';

const ImportSection = React.forwardRef<any, ImportSectionProps>(({
  config,
  onImport,
  onError,
  loading = false,
  onImportEditedData,
  isInAIAvatarLayout = false,
  showOverrideDealerIdToggle = false,
}, ref) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const selectedDealerRegular = useSelector(selectDealerSelected);
  const selectedDealerAIAvatar = useSelector(selectDealerSelectedAIAvatar);
  const selectedDealer = isInAIAvatarLayout ? selectedDealerAIAvatar : selectedDealerRegular;
  const selectedDealerId = selectedDealer?.dealer || selectedDealer?._id || '';
  const { 
    handleImport, 
    importLoading, 
    importError, 
    importData, 
    validationResult,
    importFile,
    setImportFile,
    handleCompareZip,
    compareData,
    compareLoading,
    compareError,
    handleImportCompareData,
    resetState,
    setError,
    setImportError,
    loading: contextLoading,
    setLoading,
    // Entity selection
    availableEntities,
    selectedEntity,
    entityFilters,
    entitiesLoading,
    entitiesError,
    loadEntities,
    selectEntity
  } = useImportExport();
  
  const [validateOnly, setValidateOnly] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [overrideDealerId, setOverrideDealerId] = useState(false);
  const [fileUploadKey, setFileUploadKey] = useState(0); // Key to force FileUploadMAIQ remount
  
  // Step management state
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  
  // Confirmation modal state
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [pendingImportData, setPendingImportData] = useState<any[]>([]);
  
  // Use context loading state for import operations
  const isImporting = contextLoading || importLoading;
  const isValidatingImportFile = compareLoading;
  
  // Ref to access ComparePreview's edited data
  const comparePreviewRef = useRef<ComparePreviewRef>(null);
  const previousSelectedDealerIdRef = useRef<string | null>(null);
  
  // State to preserve edited data when navigating between steps
  const [preservedEditedData, setPreservedEditedData] = useState<any[]>([]);
  const [hasEditedData, setHasEditedData] = useState(false);
  
  // State to preserve original data for diff display
  const [originalCompareData, setOriginalCompareData] = useState<any[]>([]);

  // Load entities on component mount
  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  // Store original compare data when it first arrives - deep clone to prevent mutations
  useEffect(() => {
    if (compareData && compareData.length > 0 && originalCompareData.length === 0) {
      // Deep clone the data to prevent any mutations
      const deepClonedData = JSON.parse(JSON.stringify(compareData));
      setOriginalCompareData(deepClonedData);
    }
  }, [compareData, originalCompareData.length]);

  // Reset local states function
  const resetLocalStates = useCallback(() => {
    setValidateOnly(true);
    setUpdateExisting(true);
    setFileUploadKey(prev => prev + 1); // Force FileUploadMAIQ to remount
    setOverwriteExisting(false);
    setOverrideDealerId(false);
    setConfirmationModalOpen(false);
    setPendingImportData([]);
    setCurrentStep(0);
    setCompletedSteps([]);
    setPreservedEditedData([]);
    setHasEditedData(false);
    setOriginalCompareData([]);
  }, []);

  // Complete reset function that resets both local and context states
  const resetAllStates = useCallback(() => {
    resetLocalStates();
    resetState();
    // Also reset ComparePreview if it exists
    if (comparePreviewRef.current?.resetEditingStates) {
      comparePreviewRef.current.resetEditingStates();
    }
  }, [resetLocalStates, resetState]);

  // Reset import flow whenever selected dealer changes
  useEffect(() => {
    const previousDealerId = previousSelectedDealerIdRef.current;

    // Skip reset on first mount; initialize baseline only
    if (previousDealerId === null) {
      previousSelectedDealerIdRef.current = selectedDealerId || '';
      return;
    }

    if (previousDealerId !== (selectedDealerId || '')) {
      resetAllStates();
      previousSelectedDealerIdRef.current = selectedDealerId || '';
    }
  }, [selectedDealerId, resetAllStates]);

  // Step management functions
  const goToStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const markStepCompleted = useCallback((step: number) => {
    setCompletedSteps(prev => {
      if (!prev.includes(step)) {
        return [...prev, step];
      }
      return prev;
    });
  }, []);

  const nextStep = useCallback(() => {
    // Save current edited data before moving to next step
    if (comparePreviewRef.current && currentStep === 1) {
      const editedData = comparePreviewRef.current.getEditedData();
      if (editedData && editedData.length > 0) {
        setPreservedEditedData(editedData);
        setHasEditedData(true);
      }
    }
    markStepCompleted(currentStep);
    setCurrentStep(prev => Math.min(prev + 1, 2));
  }, [currentStep, markStepCompleted]);

  const prevStep = useCallback(() => {
    // Save current edited data before going back
    if (comparePreviewRef.current && currentStep === 1) {
      const editedData = comparePreviewRef.current.getEditedData();
      if (editedData && editedData.length > 0) {
        setPreservedEditedData(editedData);
        setHasEditedData(true);
      }
    }
    setCurrentStep(prev => Math.max(prev - 1, 0));
  }, [currentStep]);

  // Function to save edited data
  const saveEditedData = useCallback(() => {
    if (comparePreviewRef.current) {
      const editedData = comparePreviewRef.current.getEditedData();
      if (editedData && editedData.length > 0) {
        setPreservedEditedData(editedData);
        setHasEditedData(true);
      }
    }
  }, []);

  // Function to restore edited data
  const restoreEditedData = useCallback(() => {
    if (hasEditedData && preservedEditedData.length > 0 && comparePreviewRef.current) {
      // Reset the ComparePreview to the preserved data
      comparePreviewRef.current.resetEditingStates();
      // The ComparePreview will use the preserved data
    }
  }, [hasEditedData, preservedEditedData]);

  // Helper function to compare objects ignoring empty/null/undefined fields
  const compareObjectsIgnoringEmpty = (obj1: any, obj2: any): boolean => {
    // Remove empty fields from both objects before comparison
    const cleaned1 = removeEmptyStringFields(obj1);
    const cleaned2 = removeEmptyStringFields(obj2);
    
    // Compare cleaned objects
    return JSON.stringify(cleaned1) !== JSON.stringify(cleaned2);
  };

  // Helper function to check if a record has any changes to display
  const hasRecordChanges = (item: any): boolean => {
    if (!item.current && !item.incoming) return false;
    
    // If no current data, it's a new record - check if incoming has any non-empty fields
    if (!item.current && item.incoming) {
      const cleanedIncoming = removeEmptyStringFields(item.incoming);
      return Object.keys(cleanedIncoming).length > 0;
    }
    
    // If no incoming data, nothing to show
    if (!item.incoming) return false;
    
    // Get all fields from both objects
    const allFields = new Set<string>();
    if (item.current) Object.keys(item.current).forEach(key => allFields.add(key));
    if (item.incoming) Object.keys(item.incoming).forEach(key => allFields.add(key));
    
    // Check if any field has changes (added or modified)
    for (const field of Array.from(allFields)) {
      const currentValue = item.current?.[field];
      const incomingValue = item.incoming?.[field];
      
      // Skip empty values
      const isEmpty = (val: any) => val === null || val === undefined || val === '';
      
      // If incoming is empty, skip (no change to show)
      if (isEmpty(incomingValue)) continue;
      
      // If current is empty but incoming has value, it's added (has change)
      if (isEmpty(currentValue) && !isEmpty(incomingValue)) {
        return true;
      }
      
      // If both have values, check if they're different (modified)
      if (!isEmpty(currentValue) && !isEmpty(incomingValue)) {
        // For objects/arrays, use JSON.stringify
        if (typeof currentValue === 'object' && typeof incomingValue === 'object') {
          if (JSON.stringify(currentValue) !== JSON.stringify(incomingValue)) {
            return true;
          }
        } else if (currentValue !== incomingValue) {
          return true;
        }
      }
    }
    
    return false;
  };

  const buildOverwritePendingData = useCallback((data: any[]) => {
    if (!data || data.length === 0) return [];

    return data
      .filter(item => item?.incoming)
      .map(item => ({
        current: null,
        incoming: item.incoming
      }));
  }, []);

  // Import edited data function (moved up for dependency order)
  const handleImportEditedData = useCallback(async (editedData: any[]) => {
    try {
      if (overwriteExisting) {
        const overwriteData = buildOverwritePendingData(editedData);
        setPendingImportData(overwriteData);
        nextStep(); // Move to step 3 (Confirm)
        return;
      }

      // Use the stored original compare data (deep cloned to prevent mutations)
      const originalData = originalCompareData;
      
      
      // Filter out entities that are identical to current data (ignoring empty fields)
      const differentEntities = editedData.filter(item => {
        if (!item.current || !item.incoming) {
          // If no current data, it's a new entity - keep it
          // But check if incoming has any non-empty fields
          const cleanedIncoming = removeEmptyStringFields(item.incoming);
          return Object.keys(cleanedIncoming).length > 0;
        }
        
        // Compare current vs incoming data, ignoring empty fields
        return compareObjectsIgnoringEmpty(item.current, item.incoming);
      });
      
      // Create diff data for confirmation step - show original current vs final incoming
      const diffData = differentEntities.map(item => {
        // Find the original data from the original compare data
        // Use a more robust matching - try to match by ID or other unique identifier first
        let originalItem = originalData.find(orig => {
          // First try to match by ID if available
          if (orig.current?._id && item.current?._id) {
            return orig.current._id === item.current._id;
          }
          // Fallback to full object comparison
          return orig.current && item.current && 
            JSON.stringify(orig.current) === JSON.stringify(item.current);
        });
        
        // If still not found, try to match by incoming data ID
        if (!originalItem && item.incoming?._id) {
          originalItem = originalData.find(orig => 
            orig.incoming?._id === item.incoming._id
          );
        }
        
        // Normalize JSON-like strings back to structured values and remove empty strings
        // so edited object/array fields preserve their original type in payload.
        const normalizedIncoming = parseJsonStringsInData(item.incoming);
        const cleanedIncoming = removeEmptyStringFields(normalizedIncoming);
        
        return {
          current: originalItem ? originalItem.current : item.current, // Original current data (unchanged)
          incoming: cleanedIncoming, // Final incoming data (after any edits and empty string removal)
          originalIncoming: originalItem ? originalItem.incoming : item.incoming // Original incoming data for reference
        };
      });
      
      // Set pending import data with diff information
      setPendingImportData(diffData);
      nextStep(); // Move to step 3 (Confirm)
    } catch (error) {
      onError(error);
      throw error;
    }
  }, [originalCompareData, nextStep, overwriteExisting, buildOverwritePendingData, onError]);

  // Function to perform the actual import
  const performImport = useCallback(async () => {
    try {
      setLoading(true);
      
      const importCandidates = overwriteExisting
        ? (pendingImportData || [])
        : (pendingImportData && pendingImportData.length > 0
            ? pendingImportData.filter(item => hasRecordChanges(item))
            : []);
      
      // Extract incoming data from selected records
      let incomingData = importCandidates
        .filter(item => item.incoming) // Only items with incoming data
        .map(item => item.incoming); // Extract the incoming data
      
      if (incomingData.length === 0) {
        throw new Error('No data to import');
      }
      
      // Final payload normalization:
      // 1) parse JSON-like string values back to objects/arrays/booleans/numbers
      // 2) remove empty string fields
      incomingData = parseJsonStringsInData(incomingData);
      incomingData = removeEmptyStringFields(incomingData);
      
      // Import the incoming data directly using the API service
      // Use selected entity if available, otherwise fallback to config.entityType
      const entityType = selectedEntity?.entity || config.entityType;
      const importParams: {
        dealer?: string;
        validate_only: boolean;
        update_existing: boolean;
        overwrite?: boolean;
      } = {
        dealer: config.dealerId,
        validate_only: false,
        update_existing: true
      };

      if (overwriteExisting) {
        importParams.overwrite = true;
      }

      const response = await ImportExportApiService.importDataFromJson(entityType, incomingData, {
        ...importParams
      });
      
      // Close confirmation modal
      setConfirmationModalOpen(false);
      
      // Log the full response for debugging
      console.log('Import API Response:', response);
      console.log('Response keys:', Object.keys(response));
      const responseAny = response as any;
      console.log('response.error:', responseAny.error);
      console.log('response.body:', response.body);
      
      // Format errors from response - convert to ValidationError[]
      const formatErrors = (errors: any): ValidationError[] => {
        if (!errors) return [];
        
        if (Array.isArray(errors)) {
          return errors.map((error, index) => {
            if (typeof error === 'string') {
              return { field: 'general', message: error, row: index + 1 };
            } else if (error && typeof error === 'object' && error.field && error.message) {
              return error as ValidationError;
            } else {
              return { field: 'general', message: String(error), row: index + 1 };
            }
          });
        } else if (typeof errors === 'string') {
          return [{ field: 'general', message: errors }];
        } else if (errors && typeof errors === 'object') {
          return [{ field: errors.field || 'general', message: errors.message || String(errors) }];
        }
        
        return [];
      };
      
      let formattedErrors: ValidationError[] = [];
      let success = false;
      let importedCount = 0;
      let updatedCount = 0;
      let failedCount = 0;
      let warnings: any[] = [];
      
      // Check if there's an error directly in response (when error occurs, body might not exist)
      if (responseAny.error) {
        // Error case - no body, error is directly in response
        console.log('Found error in response.error:', responseAny.error);
        formattedErrors = formatErrors(responseAny.error);
        success = false;
        failedCount = formattedErrors.length;
      } else if (response.body) {
        // Success case - body exists
        success = response.body.success || false;
        importedCount = response.body.imported || response.body.imported_count || 0;
        updatedCount = response.body.updated || response.body.updated_count || 0;
        failedCount = response.body.failed || response.body.failed_count || 0;
        warnings = response.body.warnings || [];
        
        // Check for errors in body
        if (response.body.error) {
          formattedErrors = formatErrors(response.body.error);
        } else if (response.body.errors) {
          formattedErrors = formatErrors(response.body.errors);
        } else if (response.body.message && !response.body.success) {
          formattedErrors = formatErrors(response.body.message);
        }
        
        // If no errors found but success is false, use a default error message
        if (formattedErrors.length === 0 && !response.body.success) {
          formattedErrors = [{ field: 'general', message: response.body.message || 'Import failed' }];
        }
      } else {
        // No body and no error - use message if available
        const errorMessage = response.message || 'Failed to import data';
        formattedErrors = [{ field: 'general', message: errorMessage }];
        success = false;
        failedCount = 1;
      }
      
      console.log('Formatted errors:', formattedErrors);
      
      // Call success callback - show modal even if there are errors
      const importResult = {
        type: 'import' as const,
        success,
        importedCount,
        updatedCount,
        failedCount: failedCount || formattedErrors.length,
        errors: formattedErrors,
        warnings,
        downloadUrl: undefined,
        // Use first error message if available, otherwise use response error or message
        message: formattedErrors.length > 0 
          ? (typeof formattedErrors[0] === 'string' ? formattedErrors[0] : formattedErrors[0].message)
          : (responseAny.error || response.body?.message || response.message || 'Import failed')
      };
      
      console.log('Calling onImport with result:', importResult);
      onImport(importResult);
    } catch (error: any) {
      // Handle API errors - show them in the modal
      setConfirmationModalOpen(false);
      
      // Log the full error for debugging
      console.log('Import Error caught:', error);
      console.log('error.response:', error?.response);
      console.log('error.responseData:', error?.responseData);
      console.log('error.originalError:', error?.originalError);
      console.log('error.originalError?.response?.data:', error?.originalError?.response?.data);
      
      // Extract error message and convert to ValidationError[]
      const formatErrors = (errors: any): ValidationError[] => {
        if (!errors) return [];
        
        if (Array.isArray(errors)) {
          return errors.map((error, index) => {
            if (typeof error === 'string') {
              return { field: 'general', message: error, row: index + 1 };
            } else if (error && typeof error === 'object' && error.field && error.message) {
              return error as ValidationError;
            } else {
              return { field: 'general', message: String(error), row: index + 1 };
            }
          });
        } else if (typeof errors === 'string') {
          return [{ field: 'general', message: errors }];
        } else if (errors && typeof errors === 'object') {
          return [{ field: errors.field || 'general', message: errors.message || String(errors) }];
        }
        
        return [];
      };
      
      let errorDetails: ValidationError[] = [];
      
      // Try to get original axios error response data
      const responseData = error?.responseData || error?.response?.data || error?.originalError?.response?.data;
      
      // Check error field directly in response.data first (when error occurs, body might not exist)
      if (responseData?.error) {
        // Error is directly in response.data.error (no body)
        console.log('Found error in responseData.error:', responseData.error);
        errorDetails = formatErrors(responseData.error);
      } else if (responseData?.body?.error) {
        // API returned error in response body
        const errorBody = responseData.body;
        if (errorBody.error) {
          errorDetails = formatErrors(errorBody.error);
        } else if (errorBody.errors) {
          errorDetails = formatErrors(errorBody.errors);
        } else if (errorBody.message) {
          errorDetails = formatErrors(errorBody.message);
        }
      } else if (responseData?.errors) {
        errorDetails = formatErrors(responseData.errors);
      } else if (responseData?.message) {
        errorDetails = formatErrors(responseData.message);
      } else if (error?.message) {
        errorDetails = formatErrors(error.message);
      }
      
      if (errorDetails.length === 0) {
        errorDetails = [{ field: 'general', message: 'Failed to import data' }];
      }
      
      console.log('Formatted error details:', errorDetails);
      
      const importResult = {
        type: 'import' as const,
        success: false,
        importedCount: 0,
        updatedCount: 0,
        failedCount: errorDetails.length || 1,
        errors: errorDetails.length > 0 ? errorDetails : [{ field: 'general', message: 'Failed to import data' }],
        warnings: [],
        downloadUrl: undefined,
        message: errorDetails.length > 0 
          ? (typeof errorDetails[0] === 'string' ? errorDetails[0] : errorDetails[0].message)
          : 'Failed to import data'
      };
      
      console.log('Calling onImport with error result:', importResult);
      onImport(importResult);
    } finally {
      setLoading(false);
    }
  }, [pendingImportData, selectedEntity, onImport, setLoading, overwriteExisting, config.entityType, config.dealerId]);

  // Confirmation modal handlers
  const handleConfirmImport = useCallback(async () => {
    setConfirmationModalOpen(false);
    setLoading(true);
    
    try {
      // Check if we have compare data (ZIP, CSV, etc.) or regular import data
      if (compareData && compareData.length > 0) {
        // For files with compare data - use handleImportEditedData
        await handleImportEditedData(pendingImportData);
      } else {
        // Fallback: For files without compare data - use handleImport
        if (importFile) {
          await handleImport(importFile, false);
          
          onImport({
            success: true,
            importedCount: importData?.length || 0,
            failedCount: 0,
            errors: validationResult?.errors || [],
            warnings: validationResult?.warnings || [],
            downloadUrl: undefined
          });
        }
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  }, [pendingImportData, setLoading, compareData, handleImportEditedData, handleImport, importFile, importData, validationResult, onImport, selectedEntity]);

  const handleCancelImport = useCallback(() => {
    setConfirmationModalOpen(false);
    setPendingImportData([]);
  }, []);

  // Expose functions through ref
  useImperativeHandle(ref, () => ({
    resetAllStates,
    resetLocalStates,
    getEditedData: () => comparePreviewRef.current?.getEditedData?.() || []
  }), [resetAllStates, resetLocalStates]);



  const handleFileSelect = useCallback(async (file: File | null) => {
    // Handle file deletion (null file)
    if (!file) {
      // Reset all local states
      resetLocalStates();
      
      // Reset all context states
      resetState();
      
      // Also reset ComparePreview if it exists
      if (comparePreviewRef.current?.resetEditingStates) {
        comparePreviewRef.current.resetEditingStates();
      }
      
      return;
    }
    
    try {
      // Clear any previous errors
      setImportError(null);
      
      // Check if entity is selected
      if (!selectedEntity) {
        throw new Error('Please select an entity type first');
      }
      
      // Validate file size for memory management BEFORE processing
      const sizeValidation = validateFileSizeWithUncompressedEstimate(file);
      
      if (!sizeValidation.isValid) {
        // Create a detailed error with suggestions
        const isZipFile = file.name.toLowerCase().endsWith('.zip');
        const fileSizeError = createFileSizeError(
          file, 
          isZipFile ? 512 * 1024 : 1024 * 1024,
          isZipFile ? 'compressed_too_large' : 'file_too_large'
        );
        
        // Log the error for debugging
        logFileSizeError(fileSizeError, 'ImportSection.handleFileSelect');
        
        // Format error for UI display with better formatting
        const errorInfo = formatErrorForUI(fileSizeError);
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        const maxSizeMB = isZipFile ? '0.5' : '1.0';
        
        // Create a professional error message
        let detailedMessage = `File size (${fileSizeMB}MB) exceeds the maximum allowed size of ${maxSizeMB}MB. Please select a smaller file.`;
        
        throw new Error(detailedMessage);
      }
      
      // For all file types (ZIP, CSV, etc.), use the same compare flow
      // This reuses the compare API which works for all file types
      const dealerIdForValidation = config.dealerId || selectedDealerId;
      await handleCompareZip(file, dealerIdForValidation, overrideDealerId);
      // Set importFile so UI shows selected file
      setImportFile(file);
      // Move to step 2 (Edit Data) after successful comparison
      nextStep();
      
    } catch (error) {
      setCurrentStep(0);
      setImportFile(null);
      setFileUploadKey(prev => prev + 1);
      
      // Set the error state directly to ensure it shows in UI
      const errorMessage = error instanceof Error ? error.message : 'Failed to validate file';

      const isDealerMismatchError =
        errorMessage.toLowerCase().includes('dealer validation failed') ||
        errorMessage.toLowerCase().includes('dealer_id') ||
        errorMessage.toLowerCase().includes('dealership_id');

      if (isDealerMismatchError) {
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'error',
            message: 'Dealer is different. Import not allowed for selected dealer.',
          }),
        );
      }

      if (error instanceof Error) {
        setImportError(errorMessage);
      }
      
      onError(error);
    }
  }, [handleCompareZip, onError, resetState, selectedEntity, dispatch, config.dealerId, selectedDealerId, nextStep, setImportError, setImportFile, overrideDealerId]);

  const handleImportClick = async () => {
    if (!importFile) return;
    
    // Always show confirmation modal first
    if (compareData && compareData.length > 0) {
      // For files with compare data (ZIP, CSV, etc.)
      const editedData = comparePreviewRef.current?.getEditedData?.() || compareData;

      if (overwriteExisting) {
        const overwriteData = buildOverwritePendingData(editedData);
        if (overwriteData.length === 0) {
          onImport({
            success: true,
            importedCount: 0,
            failedCount: 0,
            errors: [],
            warnings: [],
            downloadUrl: undefined
          });
          return;
        }

        setPendingImportData(overwriteData);
        nextStep(); // Move to step 3 (Confirm)
        return;
      }
      
      // Filter out entities that are identical to current data (ignoring empty fields)
      const differentEntities = editedData.filter(item => {
        if (!item.current || !item.incoming) {
          // If no current data, it's a new entity - keep it
          // But check if incoming has any non-empty fields
          const cleanedIncoming = removeEmptyStringFields(item.incoming);
          return Object.keys(cleanedIncoming).length > 0;
        }
        
        // Compare current vs incoming data, ignoring empty fields
        return compareObjectsIgnoringEmpty(item.current, item.incoming);
      });
      
      if (differentEntities.length === 0) {
        // No different entities to import
        onImport({
          success: true,
          importedCount: 0,
          failedCount: 0,
          errors: [],
          warnings: [],
          downloadUrl: undefined
        });
        return;
      }
      
      // Set pending import data and move to confirm step
      setPendingImportData(differentEntities);
      nextStep(); // Move to step 3 (Confirm)
    } else {
      // Fallback: For files without compare data (should be rare now)
      // Create a simple data structure for confirmation
      const simpleImportData = importData ? importData.map((item, index) => ({
        current: null, // No existing data for comparison
        incoming: item // The data to be imported
      })) : [];
      
      if (simpleImportData.length === 0) {
        // No data to import
        onImport({
          success: true,
          importedCount: 0,
          failedCount: 0,
          errors: [],
          warnings: [],
          downloadUrl: undefined
        });
        return;
      }
      
      // Set pending import data and move to confirm step
      setPendingImportData(simpleImportData);
      nextStep(); // Move to step 3 (Confirm)
    }
  };


  // Function to import edited data from ComparePreview ref
  const importEditedDataFromRef = async () => {
    if (!comparePreviewRef.current) {
      return;
    }
    
    const editedData = comparePreviewRef.current.getEditedData();
    await handleImportEditedData(editedData);
  };

  const getSupportedFileTypes = () => {
    return config.supportedFormats.map(format => `.${format}`).join(', ');
  };

  // Helper function to format entity name: replace underscores with spaces and capitalize each word
  const formatEntityName = (entityName: string): string => {
    return entityName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const renderValidationResults = () => {
    if (!validationResult) return null;

    const { errors, warnings } = validationResult;
    const errorCount = errors?.length || 0;
    const warningCount = warnings?.length || 0;
    const isValid = errorCount === 0;

    return (
      <Card elevation={1} sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Validation Results
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              icon={isValid ? <CheckCircle /> : <ErrorIcon />}
              label={`${isValid ? 'Valid' : 'Invalid'} Data`}
              color={isValid ? 'success' : 'error'}
              variant="outlined"
            />
            {errorCount > 0 && (
              <Chip
                icon={<ErrorIcon />}
                label={`${errorCount} Errors`}
                color="error"
                variant="outlined"
              />
            )}
            {warningCount > 0 && (
              <Chip
                icon={<Warning />}
                label={`${warningCount} Warnings`}
                color="warning"
                variant="outlined"
              />
            )}
          </Box>

          {errors && errors.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                Errors:
              </Typography>
              <List dense>
                {errors.slice(0, 5).map((error: any, index: number) => (
                  <ListItem key={index} sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <ErrorIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={error.message}
                      secondary={error.field ? `Field: ${error.field}` : ''}
                    />
                  </ListItem>
                ))}
                {errors.length > 5 && (
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText
                      primary={`... and ${errors.length - 5} more errors`}
                      sx={{ fontStyle: 'italic' }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

          {warnings && warnings.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="warning.main" gutterBottom>
                Warnings:
              </Typography>
              <List dense>
                {warnings.slice(0, 3).map((warning: any, index: number) => (
                  <ListItem key={index} sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Warning color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={warning.message}
                      secondary={warning.field ? `Field: ${warning.field}` : ''}
                    />
                  </ListItem>
                ))}
                {warnings.length > 3 && (
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText
                      primary={`... and ${warnings.length - 3} more warnings`}
                      sx={{ fontStyle: 'italic' }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ 
      p: 2.5,
      paddingBottom: '100px', // Add padding to prevent content from being hidden behind fixed buttons
      '@keyframes pulse': {
        '0%': {
          boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
        },
        '50%': {
          boxShadow: '0 6px 20px rgba(211, 47, 47, 0.5)',
        },
        '100%': {
          boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
        },
      }
    }}>
      {/* <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ 
          color: '#0F2A52',
          fontWeight: 600,
          fontSize: '1.1rem',
          mb: 1
        }}
      >
        Import {config.entityType.charAt(0).toUpperCase() + config.entityType.slice(1)} Data
      </Typography> */}
      
      {/* <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ 
          mb: 3,
          fontSize: '0.85rem',
          lineHeight: 1.5
        }}
      >
        Select a file to import data. Supported formats: {getSupportedFileTypes()}
        <br />
        <Typography variant="caption" sx={{ 
          color: '#6c757d', 
          fontSize: '0.75rem',
          fontStyle: 'italic'
        }}>
          Max file size: 1MB (uncompressed) / 512KB (compressed)
        </Typography>
      </Typography> */}

      {/* Step Indicator */}
      <ImportStepIndicator 
        activeStep={currentStep}
        completedSteps={completedSteps}
        isInAIAvatarLayout={isInAIAvatarLayout}
      />

      {importError && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            borderRadius: 1,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 400, color: '#721c24' }}>
            {importError}
          </Typography>
        </Alert>
      )}

      {/* Step 1: Select Entity and File */}
      {currentStep === 0 && (
        <Grid container spacing={3}>
          {/* Entity Selection */}
          <Grid item xs={12}>
            <Card 
              elevation={0}
              sx={{
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    color: '#0F2A52',
                    fontWeight: 600,
                    fontSize: '1rem',
                    mb: 2
                  }}
                >
                  Select Entity Type *
                </Typography>
                
                {entitiesError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {entitiesError}
                  </Alert>
                )}
                
                <FormControl fullWidth size="small" required>
                  <InputLabel>Choose Entity *</InputLabel>
                  <Select
                    value={selectedEntity?.entity || ''}
                    label="Choose Entity *"
                    onChange={(e) => {
                      const entity = availableEntities.find(ent => ent.entity === e.target.value);
                      if (entity) {
                        selectEntity(entity);
                      }
                    }}
                    disabled={entitiesLoading}
                    required
                  >
                    {entitiesLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading entities...
                      </MenuItem>
                    ) : (
                      availableEntities.map((entity) => (
                        <MenuItem key={entity.entity} value={entity.entity}>
                          {formatEntityName(entity.entity)}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                
                {selectedEntity && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Selected: {formatEntityName(selectedEntity.entity)} 
                    {entityFilters.length > 0 && ` (${entityFilters.length} filters available)`}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* File Upload */}
          <Grid item xs={12}>
            <Card 
              elevation={0}
              sx={{
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
                opacity: !selectedEntity ? 0.6 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    color: '#0F2A52',
                    fontWeight: 600,
                    fontSize: '1rem',
                    mb: 2
                  }}
                >
                  Upload File
                </Typography>

                {showOverrideDealerIdToggle && (
                  <FormControlLabel
                    sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}
                    control={
                      <Switch
                        checked={overrideDealerId}
                        onChange={(event) => setOverrideDealerId(event.target.checked)}
                        color="primary"
                        disabled={isImporting || isValidatingImportFile}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: '#0F2A52', fontSize: '0.95rem' }}>
                          Override dealer_id
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          When enabled, all imported records will use the currently selected dealer ID.
                        </Typography>
                      </Box>
                    }
                  />
                )}
                
                <FileUploadMAIQ
                  key={fileUploadKey}
                  selectedFile={importFile}
                  onFileSelect={handleFileSelect}
                  accept={getSupportedFileTypes()}
                  disabled={isImporting || isValidatingImportFile || !selectedEntity}
                />

                {isValidatingImportFile && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: 'rgba(15, 42, 82, 0.06)',
                      borderRadius: 1,
                      border: '1px solid rgba(15, 42, 82, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <CircularProgress size={18} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#0F2A52',
                        fontWeight: 500,
                      }}
                    >
                      Validating file and fetching comparison data...
                    </Typography>
                  </Box>
                )}
                
                {!selectedEntity && (
                  <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    backgroundColor: 'rgba(255, 193, 7, 0.1)', 
                    borderRadius: 1,
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Warning sx={{ color: '#ff9800', fontSize: 20 }} />
                    <Typography 
                      variant="body2"
                      sx={{
                        color: '#e65100',
                        fontWeight: 500
                      }}
                    >
                      Please select an entity type above to enable file upload
                    </Typography>
                  </Box>
                )}
                
                {importFile && (
                  <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    backgroundColor: 'rgba(15, 42, 82, 0.04)', 
                    borderRadius: 1,
                    border: '1px solid rgba(15, 42, 82, 0.08)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Info sx={{ color: '#0F2A52' }} />
                        <Typography 
                          variant="body2"
                          sx={{
                            color: '#0F2A52',
                            fontWeight: 500
                          }}
                        >
                          Selected: {importFile.name} ({(importFile.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleFileSelect(null)}
                        sx={{
                          color: '#d32f2f',
                          '&:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.04)',
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Validation Results */}
          {validationResult && renderValidationResults()}
        </Grid>
      )}

      {/* Step 2: Edit Data */}
      {currentStep === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={overwriteExisting}
                      onChange={(event) => setOverwriteExisting(event.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#0F2A52', fontSize: '0.95rem' }}>
                        Overwrite Existing Records
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        When enabled, only incoming records are shown and imported with overwrite mode.
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Compare Results - Show for all file types that have compareData (ZIP, CSV, etc.) */}
          {importFile && compareData && compareData.length > 0 && (
            <Grid item xs={12}>
              <ComparePreview
                ref={comparePreviewRef}
                compareData={compareData}
                loading={compareLoading}
                error={compareError}
                preservedData={hasEditedData ? preservedEditedData : null}
                isInAIAvatarLayout={isInAIAvatarLayout}
                overwriteMode={overwriteExisting}
                onAcceptChanges={async (editedData) => {
                  await handleImportEditedData(editedData);
                }}
                onRejectChanges={() => {
                  // Future implementation
                }}
                onDataChange={(index, field, value) => {
                  // Future implementation for saving changes
                }}
              />
            </Grid>
          )}

          {/* Fixed Bottom Navigation Buttons for Step 2 */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'white',
              borderTop: '1px solid #e0e0e0',
              p: 2,
              zIndex: 1000,
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Box sx={{ 
              maxWidth: '1200px', 
              margin: '0 auto',
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Button
                variant="outlined"
                onClick={prevStep}
                sx={{
                  borderColor: '#0F2A52',
                  color: '#0F2A52',
                  '&:hover': {
                    borderColor: '#0A1F3F',
                    backgroundColor: 'rgba(15, 42, 82, 0.04)',
                  },
                }}
              >
                Previous
              </Button>
              
              <Button
                variant="contained"
                onClick={async () => {
                  // Get edited data from ComparePreview and proceed to confirm
                  if (comparePreviewRef.current) {
                    const editedData = comparePreviewRef.current.getEditedData();
                    await handleImportEditedData(editedData);
                  } else {
                    // Fallback: if ComparePreview is not available, proceed directly
                    handleImportClick();
                  }
                }}
                disabled={isImporting}
                sx={{ 
                  minWidth: 120,
                  backgroundColor: isInAIAvatarLayout
                    ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                    : '#0F2A52',
                  color: isInAIAvatarLayout
                    ? (theme.palette.mode === 'light' ? '#ffffff' : '#000000')
                    : 'white',
                  '&:hover': {
                    backgroundColor: isInAIAvatarLayout
                      ? (theme.palette.mode === 'light' ? '#333333' : '#e0e0e0')
                      : '#0A1F3F',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(15, 42, 82, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                {importLoading ? 'Processing...' : 'Continue to Confirm'}
              </Button>
            </Box>
          </Box>
        </Grid>
      )}

      {/* Step 3: Confirm Import */}
      {currentStep === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card 
              elevation={0}
              sx={{
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    color: '#0F2A52',
                    fontWeight: 600,
                    fontSize: '1rem',
                    mb: 2
                  }}
                >
                  Confirm Import
                </Typography>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 3,
                    fontSize: '0.85rem',
                    lineHeight: 1.5
                  }}
                >
                  Review the data that will be imported and confirm to proceed.
                </Typography>

                {/* Import Confirmation Content - Inline instead of modal */}
                {(() => {
                  const recordsWithChanges = overwriteExisting
                    ? (pendingImportData || [])
                    : (pendingImportData && pendingImportData.length > 0
                        ? pendingImportData.filter(item => hasRecordChanges(item))
                        : []);
                  
                  return recordsWithChanges.length > 0 ? (
                    <Box>
                      {/* Summary */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ color: '#0F2A52', fontWeight: 600, mb: 1 }}>
                          Import Summary
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          <strong>{recordsWithChanges.length} records</strong> will be uploaded to your {formatEntityName(selectedEntity?.entity || config.entityType)} data.
                        </Typography>
                      </Box>

                      {/* Data Preview with GitHub-style diff */}
                      <Box sx={{ 
                        maxHeight: 600, 
                        overflow: 'auto',
                        position: 'relative',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d0d7de',
                        borderRadius: '6px',
                        p: 0
                      }}>
                        {/* GitHub-style header */}
                        <Box sx={{ 
                          backgroundColor: '#f6f8fa',
                          padding: '12px 16px',
                          borderBottom: '1px solid #d0d7de',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderTopLeftRadius: '6px',
                          borderTopRightRadius: '6px',
                          position: 'sticky',
                          top: 0,
                          zIndex: 10
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ 
                              width: '16px',
                              height: '16px',
                              backgroundColor: '#0F2A52',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Typography sx={{ color: 'white', fontSize: '0.7rem', fontWeight: 'bold' }}>
                                D
                              </Typography>
                            </Box>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#24292e', 
                                fontWeight: 600,
                                fontSize: '0.875rem'
                              }}
                            >
                              Data Preview with Changes
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            fontSize: '0.75rem',
                            color: '#656d76',
                            backgroundColor: '#ffffff',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            border: '1px solid #d0d7de'
                          }}>
                            {recordsWithChanges.length} records
                          </Box>
                        </Box>
                        
                        {/* Diff content */}
                        <Box sx={{ p: 0 }}>
                          {recordsWithChanges.map((item, index) => (
                            <JsonDiffViewer
                              key={index}
                              currentData={item.current}
                              incomingData={item.incoming}
                              recordIndex={index}
                              recordType="Record"
                              editable={false}
                              showOnlyConflicts={true}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      p: 3, 
                      textAlign: 'center', 
                      backgroundColor: 'rgba(15, 42, 82, 0.04)',
                      borderRadius: 1.5,
                      border: '1px solid rgba(15, 42, 82, 0.08)'
                    }}>
                      <Typography variant="body1" color="text.secondary">
                        No records to import.
                      </Typography>
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

      {/* Fixed Bottom Navigation Buttons */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'white',
          borderTop: '1px solid #e0e0e0',
          p: 2,
          zIndex: 1000,
          boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{ 
          maxWidth: '1200px', 
          margin: '0 auto',
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Button
            variant="outlined"
            onClick={prevStep}
            sx={{
              borderColor: '#0F2A52',
              color: '#0F2A52',
              '&:hover': {
                borderColor: '#0A1F3F',
                backgroundColor: 'rgba(15, 42, 82, 0.04)',
              },
            }}
          >
            Previous
          </Button>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setCurrentStep(0);
                setCompletedSteps([]);
                resetAllStates();
              }}
              sx={{
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': {
                  borderColor: '#b71c1c',
                  backgroundColor: 'rgba(211, 47, 47, 0.04)',
                },
              }}
            >
              Cancel
            </Button>
            
            {(() => {
              const recordsWithChanges = overwriteExisting
                ? (pendingImportData || [])
                : (pendingImportData && pendingImportData.length > 0
                    ? pendingImportData.filter(item => hasRecordChanges(item))
                    : []);
              
              return (
                <Button
                  variant="contained"
                  onClick={() => {
                    setConfirmationModalOpen(true);
                  }}
                  disabled={isImporting || recordsWithChanges.length === 0}
                  startIcon={isImporting ? <CircularProgress size={20} /> : <CheckCircle />}
                  sx={{
                    backgroundColor: isInAIAvatarLayout
                      ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                      : '#0F2A52',
                    color: isInAIAvatarLayout
                      ? (theme.palette.mode === 'light' ? '#ffffff' : '#000000')
                      : 'white',
                    '&:hover': {
                      backgroundColor: isInAIAvatarLayout
                        ? (theme.palette.mode === 'light' ? '#333333' : '#e0e0e0')
                        : '#0A1F3F',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(15, 42, 82, 0.3)',
                      color: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                >
                  {isImporting ? 'Importing...' : 
                   recordsWithChanges.length === 0 ? 'No Records to Import' : 
                   `Import ${recordsWithChanges.length} Records`}
                </Button>
              );
            })()}
          </Box>
        </Box>
      </Box>
        </Grid>
      )}

      {/* Confirmation Modal */}
      <ConfirmationDialogMAIQ
        open={confirmationModalOpen}
        onClose={() => {
          if (isImporting) return;
          setConfirmationModalOpen(false);
          resetAllStates();
        }}
        onConfirm={performImport}
        title={overwriteExisting ? 'Confirm Overwrite Import' : 'Confirm Import'}
        message={
          overwriteExisting ? (
            <>
              Are you sure you want to <strong>overwrite documents</strong> for{' '}
              <strong>
                {formatEntityName(selectedEntity?.entity || config.entityType)}
              </strong>{' '}
              entity with <strong>{pendingImportData.length} records</strong>? This
              action cannot be undone.
            </>
          ) : (
            <>
              Are you sure you want to import{' '}
              <strong>{pendingImportData.length} records</strong> to your{' '}
              {formatEntityName(selectedEntity?.entity || config.entityType)} data?
              This action cannot be undone.
            </>
          )
        }
        confirmLabel={overwriteExisting ? 'Confirm Overwrite' : 'Confirm Import'}
        confirmRequiredText={overwriteExisting ? 'overwrite' : undefined}
        loading={isImporting}
        loadingLabel="Importing..."
      />
    </Box>
  );
});

export default ImportSection;
